/**
 * Remove duplicates from an array
 * @param array the input array
 * @returns the array without duplicates
 */
export const uniq = <T>(array: T[]): T[] => [...new Set(array)];

/**
 * Remove duplicates from an array by key
 * @param array the input array
 * @param criteria the key or function to compare
 * @returns the array without duplicates
 */
export const uniqBy = <T>(array: T[], criteria: ((item: T) => string) | keyof T): T[] => {
  const seen = new Set();
  return array?.filter((item) => {
    const key = typeof criteria === 'function' ? criteria(item) : item?.[criteria];
    return seen.has(key) ? false : seen.add(key);
  });
};

/**
 * Merge multiple arrays (without duplicates)
 * @param array the arrays to merge
 * @returns the merged array
 */
export function mergeArrays<T>(...array: T[][]): T[] {
  return [...new Set([].concat(...array))];
}

/**
 * Split an array into multiple arrays of a given size
 * @param array the input array
 * @param size the size of the sub-arrays
 * @returns the array split into sub-arrays
 */
export function splitArray<T>(array: T[], size: number): T[][] {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

/**
 * Groups an array by a given key or function
 * @param array the input array
 * @param criteria the key or function to group by
 * @returns the grouped array
 */
export const groupBy = <T, K extends PropertyKey>(array: T[], criteria: ((item: T, index: number) => K) | keyof T) =>
  Object.groupBy<K, T>(array, (item, index) =>
    typeof criteria === 'function' ? criteria(item, index) : (item[criteria] as K),
  );

/**
 * Orders an array by a given key or function
 * @param array the input array
 * @param criteria the key or function to order by
 * @param order the order (asc or desc)
 * @returns the ordered array
 */
export const orderBy = <T>(array: T[], criteria: ((item: T) => any) | keyof T, order: 'asc' | 'desc' = 'asc'): T[] =>
  array?.toSorted((a, b) => {
    const keyA = typeof criteria === 'function' ? criteria(a) : a[criteria];
    const keyB = typeof criteria === 'function' ? criteria(b) : b[criteria];
    if (keyA < keyB) return order === 'asc' ? -1 : 1;
    if (keyA > keyB) return order === 'asc' ? 1 : -1;
    return 0;
  });

/**
 * Removes items from an array even if they are complex objects
 * @param array the input array
 * @param values the items to remove
 * @returns the array without the items
 */
export const without = <T>(array: T[], ...values: T[]): T[] =>
  array.filter((item) => !values.some((v) => isEqual(v, item)));

/**
 * Checks if the value is an empty object, collection, map, or set.
 *
 * @param value - The value to check.
 * @return True if value is empty, else False.
 */
export const isEmpty = <T>(value: T): boolean => {
  if (value == null || value === undefined) return true;
  if (Array.isArray(value) || typeof value === 'string') return !value.length;
  if (typeof value === 'object') {
    for (const key in value) if (value.hasOwnProperty(key)) return false;
  }
  return true;
};

/**
 * Remove propertis from an object
 * @param obj the input object
 * @param keys the keys to remove
 * @returns the object without the keys
 */
export const omit = <T>(obj: T, ...keys: (keyof T)[]): Omit<T, keyof T> =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k as keyof T))) as Omit<T, keyof T>;

const arrayRegex = /\[(\d+)\]/g;
/**
 * Get a nested property from an object
 * @param obj the input object
 * @param path the path to the property
 * @param defaultValue the default value if the property is not found
 * @returns the value of the property
 */
export const get = <T, K extends keyof T>(obj: T, path: string, defaultValue?: any): T[K] => {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (arrayRegex.test(key)) {
      result = result[key.replace(arrayRegex, '')];
      for (const index of Array.from(key.matchAll(arrayRegex), (m) => m[1])) {
        result = result[parseInt(index)];
      }
    } else {
      result = result[key];
    }
    if (result === undefined) {
      return defaultValue;
    }
  }
  return result as T[K];
};

/**
 * Check if two objects or arrays are equal
 * @param {Object} a
 * @param {Object} b
 * @return {Boolean}
 */
export const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) return a === b;
  if (a.prototype !== b.prototype) return false;

  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!isEqual(a[i], b[i])) return false;
    return true;
  }

  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => isEqual(a[k], b[k]));
};
